import * as React from 'react';
import '../commonStyles.scss';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { getChecksumReport } from '../../../redux/actions/backoffice/checksum-report-actions';
import {
    getChecksum,
    getChecksumHashAlgorithms,
    getChecksumLoading,
    getTotalChecksumItems,
} from '../../../redux/selectors/backoffice/checksum-selectors';
import { ContentComponent } from '../../../Components/ContentComponent/ContentComponent';
import {
    Button,
    GetProp,
    Input,
    Radio,
    Table,
    TablePaginationConfig,
    TableProps,
} from 'antd';
import * as i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { IChecksumData } from '../../../helpers/interfaces';
import { useCallback, useEffect, useState } from 'react';
import { getServerError } from '../../../redux/selectors/backoffice/error-selectors';
import { ErrorComponent } from '../../Errors/ErrorComponent';
import { HeaderTitleComponent } from '../../../Components/ContentComponent/HeaderTitleComponent';
import { ChecksumColumns } from './ChecksumColumns';
import { SearchOutlined } from '@ant-design/icons';
import { getDateOfLastRefresh } from '../../../redux/selectors/user-selectors';
import {getLoginRequestHeader} from "../../../utils";
import { SERVER_URL } from 'utils/config';

interface TableParams {
    pagination: TablePaginationConfig;
    sortField?: string;
    sortOrder?: string;
    filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

interface IProps {
    data: IChecksumData[];
    getReport: Function;
    t: i18next.TFunction;
    totalItems: number;
    changePagination: Function;
    pagingInfo: any;
    error: string;
    loading: boolean;
    hashAlgorithms: string[];
}

const MAX_PAGE_SIZE = 50;

export const fetchReport = async (url: string, filename = 'checksum.csv') => {
    const config = {
        method: 'GET',
        headers: {
            ...getLoginRequestHeader(),
            'Accept': 'text/csv',
        }
    };

    const response = await fetch(url, config);
    if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
    }
};

const ChecksumReportComponent = (props: IProps) => {
    const { t } = useTranslation();
    const { data, getReport, totalItems, error, loading, hashAlgorithms } = props;
    const columns: any = ChecksumColumns();
    const [tableHeight, setTableHeight] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [algorithmType, setAlgorithmType] = useState<string>('');
    const totalWidth = columns.reduce((total: any, col: any) => total + (col.width || 0), 0);
    const [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: MAX_PAGE_SIZE,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            total: totalItems,
        },
    });

    const calculateTableHeight = useCallback(() => {
        const height = window.innerHeight * 0.8;
        setTableHeight(height);
    }, []);

    const handleExportCSV = useCallback(() => {
        const baseUrl = `${SERVER_URL}/game-certified-hash`;
        const params = new URLSearchParams();
        
        params.append('pageNumber', tableParams.pagination.current?.toString() || '1');
        params.append('pageSize', tableParams.pagination.pageSize?.toString() || MAX_PAGE_SIZE.toString());
        
        if (tableParams.sortField) {
            params.append('sortBy', tableParams.sortField);
        }
        if (tableParams.sortOrder) {
            params.append('order', tableParams.sortOrder === 'ascend' ? 'ASCENDING' : 'DESCENDING');
        }
        
        if (algorithmType) {
            params.append('hashAlgorithm', algorithmType);
        }
        
        if (searchText) {
            params.append('gameName', searchText);
        }
        
        const exportUrl = `${baseUrl}?${params.toString()}`;
        fetchReport(exportUrl, 'checksum-report.csv');
    }, [tableParams, algorithmType, searchText]);

    const handleTableChange: TableProps['onChange'] = (pagination, filters, sorter) => {
        const sortField = Array.isArray(sorter) ? sorter[0]?.field : sorter?.field;
        const sortOrder = Array.isArray(sorter) ? sorter[0]?.order : sorter?.order;
        
        setTableParams({
            pagination,
            filters,
            sortField: sortField as string,
            sortOrder: sortOrder as string,
        });
        getReport({
            pagingInfo: {
                pageNumber: pagination.current,
                pageSize: pagination.pageSize,
                sortBy: sortField || 'id',
                order: sortOrder === 'ascend' ? 'ASCENDING' : 'DESCENDING',
            },
            gameName: searchText,
            algorithmType,
        });
    };

    const handleSearch = (value: string) => {
        setSearchText(value);
        getReport({
            pagingInfo: {
                pageNumber: 1,
                pageSize: tableParams.pagination.pageSize,
                sortBy: 'id',
                order: 'ASCENDING',
            },
            gameName: value,
            algorithmType,
        });
    };

    const handleAlgorithmChange = (e: any) => {
        const value = e.target.value;
        setAlgorithmType(value);
        getReport({
            pagingInfo: {
                pageNumber: 1,
                pageSize: tableParams.pagination.pageSize,
                sortBy: 'id',
                order: 'ASCENDING',
            },
            gameName: searchText,
            algorithmType: value,
        });
    };

    useEffect(() => {
        getReport({
            pagingInfo: {
                pageNumber: 1,
                pageSize: MAX_PAGE_SIZE,
                sortBy: 'id',
                order: 'ASCENDING',
            },
            gameName: searchText,
            algorithmType,
        });
        // eslint-disable-next-line 
    }, [getReport]);

    useEffect(() => {
        setTableParams((prevParams) => ({
            ...prevParams,
            pagination: {
                ...prevParams.pagination,
                total: totalItems,
            },
        }));
    }, [totalItems]);

    useEffect(() => {
        calculateTableHeight();
        window.addEventListener('resize', calculateTableHeight);

        return () => window.removeEventListener('resize', calculateTableHeight);
    }, [calculateTableHeight]);

    return (
        <ContentComponent
            header={<HeaderTitleComponent title={t('critical_checksum')} />}
            innerContent={
                error ? (
                    <ErrorComponent error={error} />
                ) : (
                    <div className="casino_performance-table-container">
                        <div className="flex items-center gap-6 mb-4">
                            <div className="flex justify-between w-full">
                                <div className="w-96">
                                    <Input
                                        className="w-full"
                                        placeholder="Search by game name"
                                        prefix={<SearchOutlined />}
                                        allowClear
                                        onChange={(e) => handleSearch(e.target.value)}
                                        value={searchText}
                                    />
                                </div>
                                {hashAlgorithms?.length > 1 && (
                                    <Radio.Group 
                                        value={algorithmType} 
                                        onChange={handleAlgorithmChange}
                                        buttonStyle="outline"
                                    >
                                        <Radio.Button key="" value="">All</Radio.Button>
                                        {hashAlgorithms.map((algorithm) => (
                                            <Radio.Button key={algorithm} value={algorithm}>{algorithm}</Radio.Button>
                                        ))}
                                    </Radio.Group>
                                )}
                                {hashAlgorithms?.length > 0 && (
                                    <Radio.Group 
                                        value={algorithmType} 
                                        onChange={handleAlgorithmChange}
                                        buttonStyle="outline"
                                    >
                                        <Radio.Button key="" value="">All</Radio.Button>
                                        {hashAlgorithms.map((algorithm) => (
                                            <Radio.Button key={algorithm} value={algorithm}>{algorithm}</Radio.Button>
                                        ))}
                                    </Radio.Group>
                                )}
                            </div>
                            <Button 
                                onClick={handleExportCSV}
                                disabled={loading}
                            >
                                Export CSV
                            </Button>
                        </div>
                        <Table
                            style={{
                                minWidth: '100%',
                                paddingBottom: '16px',
                            }}
                            className={'casino_performance-table'}
                            dataSource={data}
                            scroll={{ x: totalWidth, y: tableHeight }}
                            columns={columns}
                            onChange={handleTableChange}
                            pagination={tableParams.pagination}
                            rowKey={(record) => `${record.gameId.toString()}`}
                            footer={() => <></>}
                            loading={loading}
                        />
                    </div>
                )
            }
        />
    );
};

const mapStateToProps = (state: any) => ({
    data: getChecksum(state),
    totalItems: getTotalChecksumItems(state),
    error: getServerError(state),
    dateOfLastRefresh: getDateOfLastRefresh(state),
    loading: getChecksumLoading(state),
    hashAlgorithms: getChecksumHashAlgorithms(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getReport: (data: any) => dispatch(getChecksumReport(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChecksumReportComponent);
